import { useEffect, useState } from "react";
import { http, fmtErr } from "../lib/api";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";
import { Trophy, Sparkles, Crown, BookOpen, Dumbbell, Flame, ShieldCheck, Notebook, Layers } from "lucide-react";

const ICON = {
  trophy: Crown,
  flame: Flame,
  dumbbell: Dumbbell,
  book: BookOpen,
  notebook: Notebook,
  shield: ShieldCheck,
  layers: Layers,
};

function ChampionCard({ champ }) {
  const Icon = ICON[champ.icon] || Trophy;
  const scoreEntries = Object.entries(champ.scores);
  const bg = champ.tie ? "#FEF3C7" : champ.winner_color;
  const dark = !champ.tie;
  return (
    <div className="nb-card p-6" data-testid={`champ-${champ.category.replace(/\s+/g, "-").toLowerCase()}`} style={{ background: bg, color: dark ? "#fff" : "#171717" }}>
      <div className="flex items-center gap-3">
        {champ.winner_avatar ? (
          <img src={champ.winner_avatar} alt={champ.winner_display}
               className="w-20 h-20 sm:w-24 sm:h-24 rounded-full border-2 border-ink object-cover shrink-0 shadow-[3px_3px_0_0_#171717]" />
        ) : (
          <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-2xl bg-white/20 border-2 border-ink flex items-center justify-center shrink-0">
            <Icon size={30} strokeWidth={2.5} color={dark ? "#fff" : "#171717"} />
          </div>
        )}
        <div className="min-w-0">
          <div className="text-[10px] font-mono uppercase tracking-widest opacity-80 flex items-center gap-1">
            <Icon size={11} strokeWidth={2.5} /> {champ.category}
          </div>
          <div className="font-display font-black text-2xl sm:text-3xl mt-1 truncate">
            {champ.tie ? "TIE" : champ.winner_display}
          </div>
        </div>
      </div>
      <div className="mt-4 space-y-1 text-sm font-mono">
        {scoreEntries.map(([name, val]) => (
          <div key={name} className="flex justify-between">
            <span>{name}</span>
            <span className="font-bold">{typeof val === "number" ? Math.round(val) : val}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function Awards() {
  const { user } = useAuth();
  const [target, setTarget] = useState(user?.role === "admin" ? "adi" : user?.username);
  const [data, setData] = useState(null);
  const [champions, setChampions] = useState(null);
  const [isFinal, setIsFinal] = useState(false);
  const [avatars, setAvatars] = useState({});

  useEffect(() => {
    (async () => {
      try {
        const { data } = await http.get("/leaderboard");
        setAvatars({ adi: data.adi.avatar_url, kudrat: data.kudrat.avatar_url });
      } catch { /* non-critical: avatars just won't render */ }
    })();
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const [dash, awards] = await Promise.all([
          http.get(`/dashboard/${target}`),
          http.get(`/awards/final`),
        ]);
        setData(dash.data);
        setChampions(awards.data.champions);
        setIsFinal(awards.data.is_final);
      } catch (e) { toast.error(fmtErr(e)); }
    })();
  }, [target]);

  const bonuses = data?.bonuses || [];
  const positives = bonuses.filter((b) => b.points > 0);
  const penalties = bonuses.filter((b) => b.points < 0);

  return (
    <div>
      <div className="flex items-center gap-3 mb-6">
        <div className="w-12 h-12 rounded-xl bg-gold border-2 border-ink shadow-[3px_3px_0_0_#171717] flex items-center justify-center">
          <Trophy size={24} strokeWidth={2.5} />
        </div>
        <h1 className="font-display font-black text-3xl">Awards</h1>
      </div>

      {/* Champions */}
      <div className="nb-card p-6 md:p-8 mb-8" data-testid="champions-section" style={{ background: "linear-gradient(135deg,#FFFBEB 0%,#FFF7ED 100%)" }}>
        <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
          <div>
            <div className="text-xs font-mono uppercase tracking-widest text-neutral-500">End of challenge · Champions</div>
            <div className="font-display font-black text-2xl md:text-3xl flex items-center gap-2">
              <Sparkles size={24} /> {isFinal ? "Final Champions" : "Projected Champions"}
            </div>
          </div>
          {!isFinal && <div className="chip bg-white">Live projection — updates as more days log</div>}
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {champions?.map((c) => <ChampionCard key={c.category} champ={c} />)}
        </div>
      </div>

      {user?.role === "admin" && (
        <div className="flex gap-2 mb-6">
          <button onClick={() => setTarget("adi")} className={`nb-btn px-3 py-2 flex items-center gap-2 ${target === "adi" ? "bg-adi text-white" : "bg-white"}`} data-testid="award-tab-adi">
            {avatars.adi && <img src={avatars.adi} alt="Adi" className="w-6 h-6 rounded-full border-2 border-ink object-cover" />}
            Adi
          </button>
          <button onClick={() => setTarget("kudrat")} className={`nb-btn px-3 py-2 flex items-center gap-2 ${target === "kudrat" ? "bg-kudrat text-white" : "bg-white"}`} data-testid="award-tab-kudrat">
            {avatars.kudrat && <img src={avatars.kudrat} alt="Kudrat" className="w-6 h-6 rounded-full border-2 border-ink object-cover" />}
            Kudrat
          </button>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="nb-card p-6">
          <div className="flex items-center gap-2 mb-4"><Sparkles size={20} /><h2 className="font-display font-black text-2xl">Bonuses earned</h2></div>
          {positives.length === 0 ? <div className="text-neutral-500">No bonuses yet. Stack those streaks.</div> : (
            <ul className="space-y-2 max-h-[420px] overflow-y-auto">
              {positives.map((b) => (
                <li key={`${b.date}-${b.reason}`} className="flex items-center justify-between gap-2 border-b border-dashed border-neutral-300 py-2">
                  <div>
                    <div className="font-semibold">{b.reason}</div>
                    <div className="text-xs font-mono text-neutral-500">{b.date}</div>
                  </div>
                  <span className="chip bg-emerald-100">+{Math.round(b.points)}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
        <div className="nb-card p-6">
          <h2 className="font-display font-black text-2xl mb-4">Penalties</h2>
          {penalties.length === 0 ? <div className="text-neutral-500">Clean slate.</div> : (
            <ul className="space-y-2 max-h-[420px] overflow-y-auto">
              {penalties.map((b) => (
                <li key={`${b.date}-${b.reason}`} className="flex items-center justify-between gap-2 border-b border-dashed border-neutral-300 py-2">
                  <div>
                    <div className="font-semibold">{b.reason}</div>
                    <div className="text-xs font-mono text-neutral-500">{b.date}</div>
                  </div>
                  <span className="chip bg-rose-100">{Math.round(b.points)}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
